import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MessageSquare, CheckCircle2, AlertCircle, Send, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';

/**
 * SMS Settings — informational page. SMS is delivered by VoIP.ms on
 * the same credentials that power the call integration; there is no
 * separate provider to configure here. The page surfaces whether the
 * underlying VoIP.ms integration is connected, shows the active sender
 * DID, and offers a "Send test SMS" helper. Operators who haven't
 * picked VoIP.ms yet are linked over to Settings → VoIP.
 */
const SmsSettings = () => {
  const [voipSettings, setVoipSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testTo, setTestTo] = useState('');
  const [testBody, setTestBody] = useState('Hello from the CRM (SMS test)');
  const [sending, setSending] = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const { data } = await api.get('/voip/settings');
      setVoipSettings(data);
    } catch (err) {
      console.error('Failed to load VoIP settings:', err);
    } finally {
      setLoading(false);
    }
  };

  const sendTest = async (e) => {
    e.preventDefault();
    if (!testTo.trim() || !testBody.trim()) return;
    setSending(true);
    try {
      await api.post('/voip/test-sms', { to: testTo.trim(), message: testBody.trim() });
      toast.success(`Test SMS sent to ${testTo.trim()}`);
    } catch (err) {
      // The send-sms-raw endpoint may not exist on every deployment.
      // Fall back to a clearer error.
      const detail = err.response?.data?.details || err.response?.data?.error || err.message;
      toast.error(detail || 'Failed to send test SMS');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto" />
          <p className="mt-4 text-gray-600">Loading SMS status…</p>
        </div>
      </div>
    );
  }

  const provider = voipSettings?.provider || 'netsapiens';
  const isVoipms = provider === 'voipms';
  const voipReady = isVoipms && voipSettings?.isConfigured;

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white shadow-lg rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200 flex items-start justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <MessageSquare size={24} className="text-teal-600" />
                SMS Settings
              </h1>
              <p className="mt-1 text-sm text-gray-600">
                SMS is delivered by Gain Rhino VoIP — the same account that powers your call integration. Calls and texts share one number, so customers see consistent caller-ID.
              </p>
            </div>
            {voipReady ? (
              <div className="px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                Connected
              </div>
            ) : (
              <div className="px-3 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                Not connected
              </div>
            )}
          </div>

          {!voipReady && (
            <div className="mx-6 mt-4 p-4 bg-amber-50 border border-amber-200 rounded-md">
              <div className="flex items-start gap-3">
                <AlertCircle size={20} className="text-amber-700 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-amber-900">Gain Rhino VoIP isn't set up yet</h3>
                  <p className="text-xs text-amber-800 mt-1">
                    {isVoipms
                      ? 'Your provider is set to Gain Rhino VoIP but the credentials are incomplete.'
                      : 'SMS requires Gain Rhino VoIP — switch the provider dropdown to Gain Rhino VoIP and save your API credentials.'}
                  </p>
                  <Link
                    to="/voip-settings"
                    className="inline-block mt-2 px-3 py-1.5 bg-amber-600 text-white text-xs rounded-md hover:bg-amber-700"
                  >
                    Open VoIP Settings →
                  </Link>
                </div>
              </div>
            </div>
          )}

          {voipReady && (
            <div className="px-6 py-4 bg-gray-50 border-b border-gray-200 grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
              <div>
                <span className="block text-gray-500">Sub-account</span>
                <span className="font-medium text-gray-900 font-mono">{voipSettings.subaccount || '—'}</span>
              </div>
              <div>
                <span className="block text-gray-500">SMS number (DID)</span>
                <span className="font-medium text-gray-900 font-mono">{voipSettings.smsDid || '—'}</span>
              </div>
            </div>
          )}

          {/* Send test SMS — disabled until VoIP.ms is connected. */}
          <form onSubmit={sendTest} className="px-6 py-6 space-y-4">
            <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
              <Send size={16} /> Send test SMS
            </h2>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Destination phone</label>
              <input
                type="tel"
                value={testTo}
                onChange={(e) => setTestTo(e.target.value)}
                placeholder="+1 555 123 4567"
                disabled={!voipReady}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-mono focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:bg-gray-100"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
              <textarea
                rows={3}
                value={testBody}
                onChange={(e) => setTestBody(e.target.value)}
                disabled={!voipReady}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:bg-gray-100"
              />
              <p className="text-xs text-gray-500 mt-1">Test SMS uses the same path as lead messages — costs whatever Gain Rhino VoIP charges per SMS for the destination.</p>
            </div>
            <button
              type="submit"
              disabled={sending || !voipReady || !testTo.trim() || !testBody.trim()}
              className="px-6 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 disabled:opacity-50 flex items-center gap-2"
            >
              {sending ? <RefreshCw size={16} className="animate-spin" /> : <Send size={16} />}
              {sending ? 'Sending…' : 'Send Test SMS'}
            </button>
          </form>

          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
            <h3 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
              <CheckCircle2 size={14} className="text-green-600" /> How it works
            </h3>
            <ul className="text-sm text-gray-600 list-disc list-inside space-y-1">
              <li>Outbound SMS go through Gain Rhino VoIP using your DID as sender.</li>
              <li>Inbound SMS are polled every 30 seconds and attached to the matching lead's timeline (last-10-digit phone match).</li>
              <li>Lead → "Send SMS" from the timeline and Automation rules' "Send SMS" action both use this same path.</li>
              <li>Per-DID SMS settings (sender ID, MMS, etc.) are managed by Gain Rhino.</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SmsSettings;
